import * as React from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  Field,
  Input,
  ListStates,
  Modal,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "../components";
import { useAsync } from "../general/useAsync";
import { useStudentNames } from "../students/StudentDirectory";
import {
  getImportDetail,
  listImportHistory,
  ReceivedMark,
} from "./MarkImportHistoryStore";

function CalculationStatus({
  status,
}: {
  status: ReceivedMark["calculationStatus"];
}) {
  const label =
    status === "Complete"
      ? "Complete"
      : status === "Pending"
        ? "Pending"
        : "Needs attention";
  return (
    <Badge
      tone={
        status === "Complete"
          ? "success"
          : status === "Pending"
            ? "warning"
            : "danger"
      }
    >
      {label}
    </Badge>
  );
}

function receivedAt(value: string) {
  return new Date(value).toLocaleString();
}

export default function MarkImportHistory() {
  const [searchDraft, setSearchDraft] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [calculationStatus, setCalculationStatus] = React.useState("");
  const [includeEarlierRevisions, setIncludeEarlierRevisions] =
    React.useState(false);
  const [pages, setPages] = React.useState<Array<string | null>>([null]);
  const [selectedID, setSelectedID] = React.useState<string | null>(null);
  const cursor = pages[pages.length - 1];
  const history = useAsync(
    () =>
      listImportHistory({
        search,
        calculationStatus,
        includeEarlierRevisions,
        cursor,
      }),
    [search, calculationStatus, includeEarlierRevisions, cursor],
  );
  const names = useStudentNames(
    history.data?.items.map((item) => item.studentID) ?? [],
  );

  function refresh() {
    setPages([null]);
    history.refresh();
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-text-strong">
            Received marks
          </h3>
          <p className="mt-1 text-sm text-text-weak">
            Marks here have been saved. Calculation status shows whether their
            downstream results are ready.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={refresh}
          disabled={history.isLoading}
        >
          Refresh imports
        </Button>
      </div>
      <Card>
        <CardBody>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setSearch(searchDraft.trim());
              setPages([null]);
            }}
          >
            <Field label="Find an import" className="mb-0 min-w-0 flex-1">
              <Input
                aria-label="Find an import"
                placeholder="Assessment, subject, source or learner ID"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                maxLength={256}
              />
            </Field>
            <Field label="Calculation status" className="mb-0">
              <Select
                aria-label="Calculation status"
                value={calculationStatus}
                onChange={(event) => {
                  setCalculationStatus(event.target.value);
                  setPages([null]);
                }}
              >
                <option value="">All statuses</option>
                <option value="Complete">Complete</option>
                <option value="Pending">Pending</option>
                <option value="NeedsAttention">Needs attention</option>
              </Select>
            </Field>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
          <label className="mt-4 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeEarlierRevisions}
              onChange={(event) => {
                setIncludeEarlierRevisions(event.target.checked);
                setPages([null]);
              }}
            />
            Include earlier revisions
          </label>
          <p className="mt-2 text-xs text-text-weak">
            {includeEarlierRevisions
              ? "Showing accepted revisions, including earlier marks."
              : "Showing the latest revision from each source result."}{" "}
            Requests rejected before acceptance remain with the sending
            application.
          </p>
        </CardBody>
      </Card>
      <ListStates
        isLoading={history.isLoading}
        error={history.error}
        isEmpty={!history.data?.items.length}
        onRetry={refresh}
        empty={{
          title:
            search || calculationStatus
              ? "No imports match these filters"
              : "No marks received yet",
          hint:
            search || calculationStatus
              ? "Change the search or calculation status to try again."
              : "Once an application sends a valid mark, it will appear here. Use Connect an application to find a target.",
        }}
      >
        <Card>
          <Table>
            <Thead>
              <Tr>
                <Th>Learner</Th>
                <Th>Assessment</Th>
                <Th align="right">Mark</Th>
                <Th>Source</Th>
                <Th>Revision</Th>
                <Th>Received</Th>
                <Th>Calculation</Th>
                <Th>
                  <span className="sr-only">Details</span>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {history.data?.items.map((item) => (
                <Tr key={item.id}>
                  <Td className="max-w-xs break-words">
                    {names(item.studentID)}
                  </Td>
                  <Td>
                    <p className="font-medium">
                      {item.assessmentName || item.assessmentID}
                    </p>
                    <p className="mt-1 text-xs text-text-weak">
                      {[item.subjectName, item.academicYearName, item.cycleName]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <p className="text-xs text-text-weak">
                      {item.markTypeName}
                    </p>
                  </Td>
                  <Td align="right" className="whitespace-nowrap font-medium">
                    {item.result} / {item.total}
                  </Td>
                  <Td className="max-w-xs break-all">{item.sourceClientID}</Td>
                  <Td>
                    {item.revision}
                    {!item.isLatestRevision && (
                      <span className="block text-xs text-text-weak">
                        Earlier revision
                      </span>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap">
                    <time dateTime={item.acceptedAt}>
                      {receivedAt(item.acceptedAt)}
                    </time>
                  </Td>
                  <Td>
                    <CalculationStatus status={item.calculationStatus} />
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`View import ${item.assessmentName} revision ${item.revision}`}
                      onClick={() => setSelectedID(item.id)}
                    >
                      Details
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Card>
      </ListStates>
      {!history.error && !!history.data?.items.length && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <p>
            Page {pages.length} · {history.data.items.length} received marks
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={pages.length === 1 || history.isLoading}
              onClick={() => setPages((current) => current.slice(0, -1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={!history.data.nextCursor || history.isLoading}
              onClick={() =>
                setPages((current) => [...current, history.data!.nextCursor])
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {selectedID && (
        <ImportDetails id={selectedID} onClose={() => setSelectedID(null)} />
      )}
    </div>
  );
}

function ImportDetails({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useAsync(() => getImportDetail(id), [id]);
  const item = detail.data?.import;
  const names = useStudentNames([item?.studentID]);
  return (
    <Modal isOpen onClose={onClose} title="Received mark" size="lg">
      <ListStates
        isLoading={detail.isLoading}
        error={detail.error}
        isEmpty={!detail.data}
        onRetry={detail.refresh}
        empty={{ title: "Import unavailable" }}
      >
        {detail.data && item && (
          <div className="space-y-5">
            <div>
              <h4 className="text-lg font-semibold">{item.assessmentName}</h4>
              <p>
                {names(item.studentID)} · {item.result} / {item.total}
              </p>
              <p className="text-sm text-text-weak">
                Revision {item.revision} ·{" "}
                {item.isLatestRevision
                  ? "Latest source revision"
                  : "Earlier revision"}
              </p>
            </div>
            <Banner tone="success">
              This mark was saved on {receivedAt(item.acceptedAt)}.
            </Banner>
            <div className="space-y-2">
              <h4 className="font-semibold">Calculation</h4>
              <CalculationStatus status={item.calculationStatus} />
              {item.calculationStatus === "Pending" && (
                <p className="text-sm">
                  The mark is saved; downstream calculation is still pending.
                </p>
              )}
              {item.calculationStatus === "NeedsAttention" && (
                <p className="text-sm">
                  The mark is saved; review these calculation findings before
                  using downstream results.
                </p>
              )}
              {detail.data.calculationFindings.map((finding, index) => (
                <p key={index} className="text-sm break-words">
                  {finding.message}{" "}
                  <span className="text-text-weak">({finding.code})</span>
                </p>
              ))}
            </div>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-text-weak">Source application</dt>
                <dd className="break-all">{item.sourceClientID}</dd>
              </div>
              <div>
                <dt className="text-text-weak">Mark type</dt>
                <dd>{item.markTypeName}</dd>
              </div>
              <div>
                <dt className="text-text-weak">Subject and year</dt>
                <dd>
                  {item.subjectName} · {item.academicYearName} ·{" "}
                  {item.cycleName}
                </dd>
              </div>
            </dl>
            <details className="rounded-card border border-border p-3 text-sm">
              <summary className="cursor-pointer font-medium">
                Technical references
              </summary>
              <dl className="mt-3 space-y-3">
                {Object.entries({
                  "Receipt ID": id,
                  "Request ID": detail.data.requestID,
                  "Learner ID": item.studentID,
                  "Assessment ID": item.assessmentID,
                  "Source result": detail.data.sourceRecordID,
                  "Source version": detail.data.sourceVersion,
                  "Mapping version": detail.data.mappingVersion,
                  "Resolved enrolment": detail.data.studentModuleEnrollmentID,
                  "Authenticated user": detail.data.authenticatedSubjectID,
                  "Source actor": detail.data.sourceActorReference,
                  "Evidence reference": detail.data.evidenceReference,
                })
                  .filter(([, value]) => value)
                  .map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-text-weak">{label}</dt>
                      <dd className="break-all">{value}</dd>
                    </div>
                  ))}
              </dl>
            </details>
          </div>
        )}
      </ListStates>
    </Modal>
  );
}
